"use client";

import { memo, useMemo } from "react";
import {
  BarChart3,
  Info,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { TableCell, TableRow } from "@/shadcn-components/ui/table";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/shadcn-components/ui/context-menu";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client";
import {
  selectSymbolPnl,
  useOpenPositionsLiveMetricsStore,
} from "@/vybe-trader-client/opened-trades/stores/useOpenPositionsLiveMetricsStore";
import {
  buildWatchlistQuoteDisplay,
  getWatchlistColumnColor,
  getWatchlistColumnValue,
} from "./watchlistQuoteUtils";
import {
  getWatchlistColumnWidth,
  WATCHLIST_SYMBOL_COL_WIDTH,
  watchlistTd,
} from "./watchlistTableLayout";
import { LiveSymbolIcon, SymbolPnlBadge } from "@/components/common";

export type WatchlistContextAction =
  | "trade"
  | "chart"
  | "details"
  | "statistics"
  | "delete";

export interface WatchlistTableRowProps {
  currency: string;
  columns: string[];
  isSelected: boolean;
  onSymbolClick: (symbol: string) => void;
  onSymbolContextAction: (symbol: string, action: WatchlistContextAction) => void;
}

function WatchlistTableRowInner({
  currency,
  columns,
  isSelected,
  onSymbolClick,
  onSymbolContextAction,
}: WatchlistTableRowProps) {
  const entry = useSubscribedSymbolsStore((state) =>
    state.byCurrency.get(currency)
  );

  const symbolPnlSelector = useMemo(() => selectSymbolPnl(currency), [currency]);
  const symbolPnl = useOpenPositionsLiveMetricsStore(symbolPnlSelector);

  if (!entry) return null;

  const quote = buildWatchlistQuoteDisplay(entry);
  const { hasOpenBuy, hasOpenSell } = entry;
  const netDirection =
    hasOpenBuy && hasOpenSell
      ? null
      : hasOpenBuy
        ? "buy"
        : hasOpenSell
          ? "sell"
          : null;

  const rowClass = isSelected
    ? "h-7 cursor-pointer bg-accent/60 hover:bg-accent"
    : "h-7 cursor-pointer hover:bg-accent";

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <TableRow className={rowClass} onClick={() => onSymbolClick(quote.symbol)}>
          <TableCell
            className={`${watchlistTd} ${WATCHLIST_SYMBOL_COL_WIDTH} font-semibold`}
          >
            <span className="flex min-w-0 items-center gap-1.5 tracking-tight">
              <LiveSymbolIcon
                name={entry.symbol.currency}
                group={entry.symbol.groupName}
                description={entry.symbol.description}
              />
              {symbolPnl && (
                <SymbolPnlBadge
                  profit={symbolPnl.profit}
                  direction={netDirection}
                />
              )}
              <span className="min-w-0 truncate">{quote.symbol}</span>
            </span>
          </TableCell>
          {columns.map((column) => (
            <TableCell
              key={column}
              className={`${watchlistTd} ${getWatchlistColumnWidth(column)} text-right font-mono tabular-nums ${getWatchlistColumnColor(quote, column)}`}
            >
              {getWatchlistColumnValue(quote, column)}
            </TableCell>
          ))}
        </TableRow>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-44">
        <ContextMenuItem onClick={() => onSymbolContextAction(quote.symbol, "trade")}>
          <TrendingUp className="mr-2 h-3.5 w-3.5" />
          Trade
        </ContextMenuItem>
        <ContextMenuItem onClick={() => onSymbolContextAction(quote.symbol, "chart")}>
          <BarChart3 className="mr-2 h-3.5 w-3.5" />
          Chart
        </ContextMenuItem>
        <ContextMenuItem onClick={() => onSymbolContextAction(quote.symbol, "details")}>
          <Info className="mr-2 h-3.5 w-3.5" />
          Details
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => onSymbolContextAction(quote.symbol, "statistics")}
        >
          <BarChart3 className="mr-2 h-3.5 w-3.5" />
          Statistics
        </ContextMenuItem>
        <ContextMenuItem
          className="text-destructive"
          onClick={() => onSymbolContextAction(quote.symbol, "delete")}
        >
          <Trash2 className="mr-2 h-3.5 w-3.5" />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

export const WatchlistTableRow = memo(
  WatchlistTableRowInner,
  (prev, next) =>
    prev.currency === next.currency &&
    prev.columns.join("|") === next.columns.join("|") &&
    prev.isSelected === next.isSelected &&
    prev.onSymbolClick === next.onSymbolClick &&
    prev.onSymbolContextAction === next.onSymbolContextAction
);
